import consola from 'consola'
import { defineNuxtPlugin } from 'nuxt/app'
import posthog, { type PostHog, type PostHogConfig, type PostHogInterface } from 'posthog-js'
import { WappRemoteCfg } from '~/protos/gen/stcs_wapp.entity'
import { nextTick, ref, useNuxtApp, useRouter, useRuntimeConfig } from '#imports'

const createPosthogShim = (): PostHog => {
  const _noop = () => undefined
  return {
    capture: () => undefined,
    captureException: () => undefined,
    getFeatureFlagPayload: () => null,
    onFeatureFlags: () => undefined,
  } as unknown as PostHog
}

const runSafely = (label: string, fn: () => unknown) => {
  try {
    const result = fn()
    if (result && typeof (result as Promise<unknown>).catch === 'function') {
      ;(result as Promise<unknown>).catch((error) => {
        consola.warn(`PostHog async call failed (${label})`, error)
      })
    }
  } catch (error) {
    consola.warn(`PostHog call threw (${label})`, error)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { $config } = useNuxtApp()
  /**
   * Default config in case remote config fails to load due to network issues e.g. dns block via pihole
   */
  // This is a FALLBACK for the case that posthog is being blocked or no FF is set.
  const defaultProdConfig: WappRemoteCfg = {
    wapp_core_cfg: {
      lp_url: 'https://dev.statycs.com',
    },
    ui: {
      dashboard: {
        active: true,
      },
      plan: {
        active: true,
      },
      financials: {
        active: true,
      },
      analysis: {
        active: true,
        sub_section_map: {
          pnl: true,
          revenues: true,
          cf_n_debt: true,
          nwc: true,
          bs: true,
          kpis: true,
          accounts: true,
        },
      },
      settings: {
        active: true,
      },
      settings_billing: {
        active: true,
      },
    },
    stripe: {
      payment_url_annually: 'https://buy.stripe.com/7sYfZi02A0mQ5IscJe5ZC08',
      payment_url_monthly: 'https://buy.stripe.com/cNicN64iQedG7QA38E5ZC09',
      payment_url_customer_portal: 'https://billing.stripe.com/p/login/3cs00reIX9zz9BCbII',
    },
  }
  // This is a FALLBACK for the case that posthog is being blocked or no FF is set.
  const defaultDevConfig: WappRemoteCfg = {
    wapp_core_cfg: {
      lp_url: 'https://statycs.com',
    },
    ui: {
      dashboard: {
        active: true,
      },
      plan: {
        active: true,
      },
      financials: {
        active: true,
      },
      analysis: {
        active: true,
        sub_section_map: {
          pnl: true,
          revenues: false,
          cf_n_debt: false,
          nwc: false,
          bs: false,
          kpis: false,
          accounts: false,
        },
      },
      settings: {
        active: true,
      },
      settings_billing: {
        active: true,
      },
    },
    stripe: {
      payment_url_annually: 'https://buy.stripe.com/7sYfZi02A0mQ5IscJe5ZC08',
      payment_url_monthly: 'https://buy.stripe.com/cNicN64iQedG7QA38E5ZC09',
      payment_url_customer_portal: 'https://billing.stripe.com/p/login/test_6oE7vbadY2U1cmc8wx',
    },
  }

  const runtimeConfig = useRuntimeConfig()
  const envKey = runtimeConfig.public.stcsEnv
  const defaultConfig = envKey === 'dev' ? defaultDevConfig : defaultProdConfig
  const wappRemoteCfg = ref<WappRemoteCfg>(WappRemoteCfg.fromJSON(defaultConfig))
  const isClient = import.meta.client
  const phShim = createPosthogShim()

  if (!isClient) {
    return {
      provide: {
        posthog: () => phShim,
        wappRemoteCfg,
      },
    }
  }

  const isLocalMode = import.meta.dev
  let ph: PostHog = phShim

  const posthogConfig: Partial<PostHogConfig> = isLocalMode
    ? {
        api_host: runtimeConfig.public.posthogHost,
        capture_pageview: false,
        enable_recording_console_log: false,
        disable_persistence: true,
        disable_surveys: true,
        disable_session_recording: true,
        disable_scroll_properties: true,
        disable_web_experiments: true,
      }
    : {
        api_host: runtimeConfig.public.posthogHost,
        capture_pageview: true,
        enable_recording_console_log: true,
        loaded(client: PostHogInterface) {
          const version = (client as PostHog).version ?? 'unknown'
          consola.success('PostHog initialize, posthog.client.version:', version)
        },
      }

  try {
    ph = posthog.init(runtimeConfig.public.posthogPublicKey, posthogConfig)
  } catch (error) {
    consola.warn('PostHog initialization failed, continuing in shim mode', error)
    ph = phShim
  }

  runSafely('setup onFeatureFlags', () =>
    ph.onFeatureFlags(() => {
      runSafely('onFeatureFlags handler', () => {
        const json = ph.getFeatureFlagPayload(envKey)
        if (json) {
          consola.debug('Remote config loaded for endpoint:', envKey, json)
          wappRemoteCfg.value = WappRemoteCfg.fromJSON(json)
        } else {
          consola.warn('Remote config missing – falling back to defaults')
          runSafely('failed_load_remote_config', () => ph.capture('failed_load_remote_config', { env: envKey }))
        }
      })
    }),
  )

  const router = useRouter()
  router.afterEach((to) => nextTick(() => runSafely('$pageview capture', () => ph.capture('$pageview', { current_url: to.fullPath }))))

  nuxtApp.hook('vue:error', (error) => {
    runSafely('captureException vue:error', () => ph.captureException(`WAPP v${$config.public.appVersion} | vue:error | ${error}`))
  })

  return {
    provide: {
      posthog: () => ph,
      wappRemoteCfg,
    },
  }
})
